<template>
  <div>
    <core-popup
      v-if="show"
      ref="corePopup"
      :title="!!settingsSync ? locale(`${integrationName}_integration_settings_up_header`) : locale(`${integrationName}_integration_popup_header`)"
      :show="show"
      :width="'540'"
    >
      <template #body>
        <div
          class="content"
        >
          <div
            v-if="syncError.isShow"
            :class="$style.integration_warning_message"
          >
            <div :class="$style.integration_warning_message_icon">
              <svg-sprite
                :name="'warning-1'"
                :size="20"
                :type="'bold'"
                :color="'#FF9A00'"
              />
            </div>

            <div :class="$style.integration_warning_message_text">
              {{ locale(syncError.message) }}
            </div>
          </div>
          <div
            :class="$style.options"
          >
            <div
              v-for="option in options"
              :class="$style.radioOption"
            >
              <vgp-radio-button
                :checked="option.selected"
                :label="locale(`integration_option_${option.id}`)"
                @update:checked="selectOption($event, option)"
              />
              <div
                v-if="option.info"
                :class="$style.labelWrapper"
              >
                <tooltip
                  :content="locale(`integration_option_${option.info}_desc`)"
                  position="right"
                >
                  <template #body>
                    <svg-sprite
                      :color="'#B2B2B2'"
                      :name="'info'"
                      :size="20"
                    />
                  </template>
                </tooltip>
              </div>
            </div>
          </div>

          <div :class="$style.sync_dropdowns_container">
            <vgp-label-slot
              :class="$style.sync_dropdown"
              :label="`${locale('integration_project_to_sync_label')} *`"
            >
              <template #body>
                <dropdown-select
                  :drop-list="projects"
                  :placeholder="locale('integration_project_dropdown_placeholder')"
                  :no-matches-message="locale('integration_project_dropdown_no_matches')"
                  :selected="selectedProject"
                  :multiselect="false"
                  :use-search="true"
                  :track-by="'id'"
                  item-template="text"
                  track-by-text="name"
                  :show-selected-first="true"
                  :show-reset-btn="true"
                  :disabled="disabledElements.projectDrop"
                  @onChange="onChangeProjectSelected"
                />
              </template>
            </vgp-label-slot>
            <vgp-label-slot
              :class="[$style.sync_dropdown, $style.sync_dropdown_last]"
              :label="`${locale('integration_sync_options_label')} *`"
            >
              <template #body>
                <dropdown-select
                  :class="$style.sync_dropdown_custom"
                  :drop-list="syncOptions"
                  :placeholder="locale('integration_sync_options_dropdown_placeholder')"
                  :selected="selectedSyncOption"
                  :multiselect="false"
                  :use-search="false"
                  :track-by="'id'"
                  track-by-text="name"
                  :show-selected-first="false"
                  :show-reset-btn="false"
                  :item-template="'withIcon'"
                  :disabled="disabledElements.syncDrop"
                  :track-by-additional-for-item="'advanced'"
                  @onChange="onChangeSyncSelected"
                />
              </template>
            </vgp-label-slot>
            <div
              v-if="selectedSyncOption && (selectedSyncOption.id === 'assigned_to_me' || selectedSyncOption.id === assignedAndMilestones)"
              :class="$style.switch_item"
            >
              <vgp-checkbox
                :size="16"
                :checked="toggleOn"
                :is-disabled="disabledElements.toggleMilestones"
                label-position="right"
                :style="disabledElements.toggleMilestones ? {'opacity': '0.5'} : {}"
                :label="locale('integration_switch_to_milestones') "
                @update:checked="()=>{disabledElements.toggleMilestones ? null : activateElement()}"
              />
            </div>

            <div :class="[$style.filter]">
              <div :class="[$style.filter_title]">
                <span>{{ locale('integration_filter_title') }}</span>
                <div />
              </div>
              <div :class="[$style.filter_row]">
                <div>
                  <vgp-label-slot
                    :class="$style.sync_dropdown"
                    :label="filterStaticItems.status.label"
                  >
                    <template #body>
                      <dropdown-select
                        :drop-list="statusOptions"
                        :placeholder="filterStaticItems.status.placeholder"
                        :no-matches-message="locale('integration_project_status_no_matches')"
                        :selected="selectedStatus"
                        :multiselect="true"
                        item-template="withIcon"
                        track-by-text="value"
                        track-by-icon-name="icon"
                        track-by-bg-color="color"
                        :show-selected-first="false"
                        :show-reset-btn="true"
                        :disabled="checkDisabledElements"
                        @onChange="onChangeStatus"
                      />
                    </template>
                  </vgp-label-slot>
                </div>
                <div>
                  <vgp-label-slot
                    :class="$style.sync_dropdown"
                    :label="filterStaticItems.priority.label"
                  >
                    <template #body>
                      <dropdown-select
                        :drop-list="priorityOptions"
                        :placeholder="filterStaticItems.priority.placeholder"
                        :no-matches-message="locale('integration_project_priority_no_matches')"
                        :selected="selectedPriority"
                        :multiselect="true"
                        item-template="withIcon"
                        track-by-text="value"
                        track-by-icon-name="icon"
                        track-by-bg-color="color"
                        :show-selected-first="false"
                        :show-reset-btn="true"
                        :disabled="checkDisabledElements"
                        @onChange="onChangePriority"
                      />
                    </template>
                  </vgp-label-slot>
                </div>
              </div>

              <div :class="[$style.filter_row]">
                <div>
                  <vgp-label-slot
                    :class="[$style.sync_dropdown]"
                    :label="filterStaticItems.startDate.label"
                  >
                    <template #body>
                      <div
                        :class="$style.input"
                        @click="() => !checkDisabledElements ? showDateRangePicker('startDate') : null"
                      >
                        <vgp-input
                          ref="startDate"
                          :placeholder="filterStaticItems.startDate.placeholder"
                          :readonly="false"
                          :disabled-input-only="true"
                          :model-value="startDatesValues"
                          :small="true"
                          :is-disabled="checkDisabledElements"
                          :icons-right="setStartDateIcons"
                          @update:modelValue="onChangeStartDate"
                        />
                      </div>
                    </template>
                  </vgp-label-slot>
                </div>
                <div>
                  <vgp-label-slot
                    :class="[$style.sync_dropdown]"
                    :label="filterStaticItems.endDate.label"
                  >
                    <template #body>
                      <div
                        :class="$style.input"
                        @click="() => !checkDisabledElements && !startDateAndMilestoneCheck ? showDateRangePicker('endDate') : null"
                      >
                        <vgp-input
                          ref="endDate"
                          :placeholder="filterStaticItems.endDate.placeholder"
                          :readonly="false"
                          :disabled-input-only="true"
                          :is-disabled="startDateAndMilestoneCheck || checkDisabledElements"
                          :model-value="endDatesValues"
                          :small="true"
                          :icons-right="setEndDateIcons"
                          @update:modelValue="onChangeEndDate"
                        />
                      </div>
                    </template>
                  </vgp-label-slot>
                </div>
              </div>

              <div :class="[$style.filter_row]">
                <div>
                  <vgp-label-slot
                    :class="$style.sync_dropdown"
                    :label="filterStaticItems.assignee.label"
                  >
                    <template #body>
                      <dropdown-select
                        :drop-list="assigneeOptions"
                        :placeholder="filterStaticItems.assignee.placeholder"
                        :no-matches-message="locale('integration_project_assignee_no_matches')"
                        :selected="selectedAssignee"
                        :multiselect="true"
                        :use-select-all="true"
                        :use-search="true"
                        :track-by="'id'"
                        item-template="user"
                        track-by-text="name"
                        :show-selected-first="true"
                        :show-reset-btn="true"
                        :disabled="disabledElements.assignee || checkDisabledElements"
                        group-by="group"
                        @onChange="onChangeAssignee"
                      />
                    </template>
                  </vgp-label-slot>
                </div>
              </div>
            </div>

            <div :class="$style.action_buttons">
              <vgp-button
                :type="'secondary'"
                :label="locale('common_cancel')"
                :width="'90px'"
                small
                @onClick="handleCancel"
              />
              <vgp-button
                :type="'primary'"
                :class="$style.action_btn"
                :label="settingsSync ? locale('common_save') : locale('integration_create')"
                :disabled="disabledElements.sendButton"
                :width="'90px'"
                small
                @onClick="handleCreateOrUpdate"
              />
            </div>
          </div>
          <div v-if="isShowDateRange">
            <vgp-date-range-picker
              v-click-outside="clickOutsideDateRange"
              :class="['filter_item_date']"
              :show="isShowDateRange"
              :show-date-range-presets="false"
              :range-data="dateRangeType === 'startDate' ? startDate : endDate"
              :min-date="setMinDate"
              :max-date="setMaxDate"
              :parent-element="dateRangeTargetParent"
              :target-element="dateRangeTarget"
              :is-range="true"
              @onChange="onChangeRange"
              @onClose="()=>hideDateRange(dateRangeType)"
            />
          </div>
        </div>
      </template>
    </core-popup>
  </div>
</template>

<script>

import _ from 'lodash';
import moment from 'moment';
import projectComponent from '../../../components/Project';
import projectsModel from '$$projectModel';
import app from '$$app';
import filterConstants from '../../filter/constants';
import filterStaticItems from '../../filter/filterStaticItems';
import filterHelpers from '../../filter/helpers';
import VgpRadioButton from '../../common/VueComponents/radioButton/vgpRadioButton.vue';
import dropdownSelect from '../../common/VueComponents/dropdownSelect/dropdownSelect.vue';
import vgpLabelSlot from '../../common/VueComponents/label/vgpLabelSlot.vue';
import CorePopup from '../../common/VueComponents/popups/corePopup/corePopup.vue';
import Tooltip from '$$vueCmp/tooltips/tooltip.vue';
import vgpCheckbox from '$$vueCmp/checkbox/vgpCheckbox.vue';
import vgpDateRangePicker from '$$vueCmp/dateRangePicker/vgpDateRange.vue';

export default {
  name: 'IntegrationPopup',
  components: {
    vgpDateRangePicker,
    vgpCheckbox,
    Tooltip,
    CorePopup,
    dropdownSelect,
    VgpRadioButton,
    vgpLabelSlot,
  },
  props: {
    show: {
      type: Boolean,
      required: true,
    },
    integrationName: {
      type: String,
      required: false,
      default: '',
    },
    existedLinks: {
      type: Array,
      required: true,
      default: [],
    },
    settingsSync: {
      type: Object,
      required: false,
      default: {},
    },

  },
  emits: [
    'createOrUpdateLink',
    'closePopup',
  ],
  data() {
    return {
      locale: __,
      filterStaticItems,
      projects: [],
      options: [{ id: 'project', selected: false }, { id: 'all_tasks', info: 'all_tasks', selected: false }],
      syncOptions: [{
        id: 'all_tasks_project',
        icon: {
          name: 'list-view',
          size: 24,
          type: 'regular',
          color: '#b2b2b2',
        },
        advanced: {
          preset: 'text',
          text: __('all_tasks_advanced_sync'),
          txtColor: '#b2b2b2',
        },
        name: __('all_tasks_sync'),
      }, {
        id: 'assigned_to_me',
        icon: {
          name: 'task',
          size: 24,
          type: 'regular',
          color: '#b2b2b2',
        },
        name: __('assigned_to_me_sync'),
      }, {
        id: 'only_milestones',
        icon: {
          name: 'milestone',
          size: 24,
          type: 'regular',
          color: '#b2b2b2',
        },
        name: __('milestones_sync'),
      }],
      dateRangeTarget: null,
      dateRangeTargetParent: null,
      assignedAndMilestones: 'assigned_to_me_and_milestones',
      selectedProject: null,
      selectedStatus: [],
      selectedPriority: [],
      selectedAssignee: [],
      selectedSyncOption: null,
      filterStartDate: null,
      filterEndDate: null,
      toggleOn: false,
      disabledElements: {
        projectDrop: true,
        syncDrop: true,
        sendButton: true,
        assignee: false,
        toggleMilestones: true,
      },
      isShowDateRange: false,
      dateRangeType: null,
      disableProjectDrop: true,
      disableSyncDrop: true,
      disabledFilter: true,
      disableButton: true,
      connectionType: null,
      syncAlreadyCreated: false,
      syncError: {
        isShow: false,
        message: 'integration_warning_message',
      },
      disableToggle: false,
      selectedOption: null,
      appOnIds: [],
      statusOptions: [],
      priorityOptions: [],
      assigneeOptions: [],
      startDate: { start: '', end: '' },
      endDate: { start: '', end: '' },
    };
  },
  computed: {
    startDatesValues() {
      let value = '';
      let startValue = '';
      let endValue = '';

      if (this.startDate.start) {
        startValue = moment(this.startDate?.start).format(user.dateFormat);
      }

      if (this.startDate.end) {
        endValue = moment(this.startDate?.end).format(user.dateFormat);
      }

      if (startValue && endValue) {
        value = `${startValue} - ${endValue}`;
      } else {
        value = `${startValue}`;
      }

      return value;
    },

    endDatesValues() {
      let value = '';
      let startValue = '';
      let endValue = '';

      if (this.endDate.start) {
        startValue = moment(this.endDate?.start).format(user.dateFormat);
      }

      if (this.endDate.end) {
        endValue = moment(this.endDate?.end).format(user.dateFormat);
      }

      if (startValue && endValue) {
        value = `${startValue} - ${endValue}`;
      } else {
        value = `${startValue}`;
      }

      return value;
    },

    setMinDate() {
      if (this.dateRangeType === 'endDate') {
        if (this.startDate.start && this.startDate.end) {
          return new Date(new Date(this.startDate.end).getTime() + 1000);
        } if (this.startDate.start) {
          return new Date(this.startDate.start);
        }

        return undefined;
      }
    },

    setMaxDate() {
      if (this.dateRangeType === 'startDate') {
        if (this.endDate.start && this.endDate.end) {
          return new Date(new Date(this.endDate.end).getTime() - 3600 * 1000 * 24);
        } if (this.endDate.start) {
          return new Date(this.endDate.start);
        }

        return undefined;
      }
    },

    setStartDateIcons() {
      if (this.startDatesValues) {
        return [
          { name: 'close-5', type: 'bold', handler: () => this.resetDatesValues('startDate') },
          {
            name: 'calendar', type: 'regular', size: 24, handler: () => this.showDateRangePicker('startDate'),
          },
        ];
      }

      return [
        {
          name: 'calendar', type: 'regular', size: 24, handler: () => this.showDateRangePicker('startDate'),
        },
      ];
    },

    setEndDateIcons() {
      if (this.endDatesValues && this.selectedSyncOption && this.selectedSyncOption.id === 'only_milestones' && this.connectionType !== 'all_tasks') {
        return [
          {
            name: 'calendar', type: 'regular', size: 24, handler: () => this.showDateRangePicker('endDate'),
          },
        ];
      }

      if (this.endDatesValues) {
        return [
          { name: 'close-5', type: 'bold', handler: () => this.resetDatesValues('endDate') },
          {
            name: 'calendar', type: 'regular', size: 24, handler: () => this.showDateRangePicker('endDate'),
          },
        ];
      }

      return [
        {
          name: 'calendar', type: 'regular', size: 24, handler: () => this.showDateRangePicker('endDate'),
        },
      ];
    },

    checkDisabledElements(type) {
      if (this.connectionType === 'all_tasks') {
        return false;
      } if (this.connectionType !== 'all_tasks' && (this.selectedProject === undefined || this.selectedProject === null)) {
        return true;
      }

      return false;
    },

    startDateAndMilestoneCheck() {
      if (this.selectedSyncOption && this.selectedSyncOption.id === 'only_milestones' && this.connectionType !== 'all_tasks') {
        return true;
      }

      return false;
    },
  },

  beforeUnmount() {
    this.appOnIds.forEach(id => {
      app.off(id);
    });
  },

  created() {
    userExtAnalytics.log('calendar_sync_connect_clicked', {
      integration_type: this.integrationName === 'google' ? 'Google Calendar' : 'Outlook Calendar',
    });

    this.init();
    this.appOnIds.push(app.on('onAfterCollaboration', data => {
      if (data.event === 'ProjectDeleted' || data.event === 'ProjectArchived') {
        this.removeProjectFromList(data.projects[0]);
      }

      if (data.event === 'ProjectRestored') {
        this.addProjectToList(+data.projects[0]);
      }

      if ((data.event === 'ResourceDeleted' || data.event === 'ResourcesOnProjectsConverted') && this.selectedProject?.id) {
        const resourcesIds = data.resources || [data.resourcesFromProjects[0].resourceId];
        const resources = [...this.$store.getters['resourcesModel/getResourcesByProjectId'](this.selectedProject.id).map(item => {
          item.img = item.photo;
          if (item.userId) {
            item.group = __('integration_assignee_group_real');
          } else {
            item.group = __('integration_assignee_group_nonreal');
          }

          return item;
        })];

        this.selectedAssignee = this.selectedAssignee.filter(resource => !resourcesIds.includes(resource.id));

        this.assigneeOptions = resources;
      }

      if (data.event === 'ProjectUpdated' || data.event === 'ResourceOnProjectsUpdated' || data.event === 'RoleAccountUpdated'
          || data.event === 'ResourceToProjectsAssigned' || data.event === 'ResourceFromProjectsUnassigned' || data.event === 'ProjectCreated'
          || data.event === 'ResourcesOnProjectsConverted') {
        const projectsData = projectComponent.getProjectsList();

        this.projects = _.cloneDeep(projectsData.filter(item => item.type === 'project' && !item.is_jira));

        if (this.selectedProject?.id === data.projects[0]) {
          this.selectedProject = this.projects.find(i => i.id === data.projects[0]);
        }

        // if (data.event === 'ResourceFromProjectsUnassigned' && this.selectedProject?.id === data.projects[0]) {
        //   this.selectedProject = null;
        //   this.disableButton = true;
        // }

        if (data.event === 'ResourceFromProjectsUnassigned' && this.selectedProject?.id === data.projects[0]) {
          const ids = data.data.resourcesFromProjects.map(res => res.resourceId);

          this.selectedAssignee = this.selectedAssignee.filter(resource => !ids.includes(resource.id));

          this.assigneeOptions = this.assigneeOptions.filter(resource => !ids.includes(resource.id));
        }

        if (data.event === 'ResourceToProjectsAssigned' && this.selectedProject?.id === data.projects[0]) {
          const resources = [...this.$store.getters['resourcesModel/getResourcesByProjectId'](this.selectedProject.id).map(item => {
            item.img = item.photo;
            if (item.userId) {
              item.group = __('integration_assignee_group_real');
            } else {
              item.group = __('integration_assignee_group_nonreal');
            }

            return item;
          })];

          this.assigneeOptions = resources;
        }
      }
    }));
    this.appOnIds.push(app.on('project:archive', (ganttId, isArchived, isCollaboration) => {
      if (isCollaboration && isArchived === 0) return;
      if (isArchived === 1) {
        this.removeProjectFromList(ganttId);
        if (this.selectedProject?.id === ganttId) {
          this.selectedProject = null;
          this.disableButton = true;
        }
      } else if (isArchived === 0) {
        this.addProjectToList(ganttId);
      }
    }));
    this.appOnIds.push(app.on('project:del', projectData => {
      this.removeProjectFromList(projectData.gantt_id);
      if (this.selectedProject?.id === projectData.gantt_id) {
        this.selectedProject = null;
        this.disableButton = true;
      }
    }));
  },
  mounted() {
    setTimeout(() => {
      this.dateRangeTargetParent = this.$refs.corePopup.$refs.popup_container;
    }, 0);
    this.statusOptions = filterConstants.STATUS_OPTIONS;
    this.priorityOptions = filterConstants.PRIORITY_OPTIONS;

    if (this.settingsSync) {
      const link = this.settingsSync;

      this.connectionType = link.connectionType;
      if (link.connectionType !== 'all_tasks') {
        this.selectedProject = this.projects.find(project => project.id === this.settingsSync.projects[0].ganttId);
        // fill assigneeOptions

        let data = [
          {
            id: '-1',
            name: __('notAssigned'),
            img: null,
          },
        ];

        if (this.selectedProject) {
          data = [...data, ...this.$store.getters['resourcesModel/getResourcesByProjectId'](this.selectedProject.id).map(item => {
            item.img = item.photo;
            if (item.userId) {
              item.group = __('integration_assignee_group_real');
            } else {
              item.group = __('integration_assignee_group_nonreal');
            }

            return item;
          })];

          this.assigneeOptions = data;
        }
      } else {
        const userResource = this.$store.getters['resourcesModel/getResourceByUserId'](link.userId);

        this.assigneeOptions = [{ ...userResource, img: userResource.photo }];
        this.disabledElements = {
          projectDrop: true,
          syncDrop: true,
          sendButton: false,
          assignee: true,
          toggleMilestones: true,
        };
      }

      // set all filter fields
      if (link.filter) {
        this.fillExistFilter(link.filter);
      } else if (!link.filter && (link.connectionType === 'all_tasks' || link.connectionType === this.assignedAndMilestones || link.connectionType === 'assigned_to_me')) {
        // todo: remove in the future.  it's for old connection
        const userResource = this.$store.getters['resourcesModel/getResourceByUserId'](link.userId);

        this.selectedAssignee = this.assigneeOptions.filter(item => item.id === userResource.id);
      }

      this.options.forEach(option => {
        if (this.settingsSync.connectionType === 'all_tasks' && option.id === 'all_tasks') {
          option.selected = true;
          // this.connectionType = link.connectionType;
          // this.syncError.isShow = true;
        } else if (link.connectionType !== 'all_tasks' && option.id === 'project') {
          option.selected = true;
          this.disabledElements = {
            projectDrop: false,
            syncDrop: false,
            sendButton: false,
            assignee: false,
            toggleMilestones: true,
          };
          // this.disableSyncDrop = false;
          // this.disableProjectDrop = false;
          // this.disabledFilter = false;
          // this.connectionType = this.settingsSync.connectionType;
          if (link.connectionType === this.assignedAndMilestones) {
            this.selectedSyncOption = this.syncOptions.find(i => i.id === 'assigned_to_me');
            this.disabledElements.toggleMilestones = false;
            this.disabledElements.assignee = true; // disable assign resources
            this.toggleOn = true;
          } else {
            this.selectedSyncOption = this.syncOptions.find(i => i.id === link.connectionType);
            if (link.connectionType === 'assigned_to_me') {
              this.disabledElements.assignee = true;
              this.disabledElements.toggleMilestones = false;
            }
          }
        }
      });
    } else if (this.projects && this.projects.length > 0) {
      this.options[0].selected = true;
      this.selectedOption = 'project';
      this.assigneeOptions = [];
      this.disabledElements = {
        ...this.disabledElements,
        projectDrop: false,
        syncDrop: false,
        toggleMilestones: true,
      };
    } else {
      this.options[1].selected = true;
      this.selectedOption = 'all_tasks';
      this.connectionType = 'all_tasks';
      this.disabledElements = {
        ...this.disabledElements,
        projectDrop: true,
        syncDrop: true,
        sendButton: false,
        toggleMilestones: true,
      };

      this.checkAssign();
    }
  },
  methods: {
    init() {
      const projectsData = projectComponent.getProjectsList();

      this.projects = _.cloneDeep(projectsData.filter(item => item.type === 'project' && !item.is_jira));
    },

    fillExistFilter(filter) {
      this.selectedStatus = filter.status ? filterConstants.STATUS_OPTIONS.filter(item => filter.status.includes(+item.id)) : [];
      this.selectedPriority = filter.priority ? filterConstants.PRIORITY_OPTIONS.filter(item => filter.priority.includes(+item.id)) : [];
      this.selectedAssignee = filter.assignee ? this.assigneeOptions.filter(item => filter.assignee.includes(+item.id)) : [];
      this.startDate = filter.startDate ? filter.startDate : { start: '', end: '' };
      this.endDate = filter.endDate ? filter.endDate : { start: '', end: '' };
    },

    selectOption(event, item) {
      this.options.forEach(option => {
        option.selected = option.id === item.id;
        this.selectedOption = item.id;
      });

      this.showErrorMessage(false);

      if (item.id === 'all_tasks') {
        this.disabledElements = {
          ...this.disabledElements,
          projectDrop: true,
          syncDrop: true,
          sendButton: false,
          toggleMilestones: true,
        };
      } else {
        this.disabledElements = {
          ...this.disabledElements,
          projectDrop: false,
          syncDrop: false,
          toggleMilestones: false,
        };
        if (this.selectedProject && this.selectedSyncOption) {
          this.disabledElements.sendButton = false;
        } else {
          this.disabledElements.sendButton = true;
        }
      }
      this.connectionType = item.id;

      this.checkAssign();

      userExtAnalytics.log('calendar_sync_tab_selected', {
        integration_type: this.integrationName === 'google' ? 'Google Calendar' : 'Outlook Calendar',
        tab: item.id === 'all_tasks' ? 'My tasks' : 'Project',
      });
    },

    showErrorMessage(status, message = 'integration_warning_message') {
      this.syncError = {
        isShow: status,
        message,
      };
    },

    validateData(data) {
      let existedLinks;

      if (data.connectionType === null || data.connectionType === undefined) {
        this.showErrorMessage(true, 'integration_error_sync_type');

        return false;
      }

      if (data.connectionType === 'all_tasks') {
        existedLinks = this.existedLinks.filter(link => link.connectionType === data.connectionType);
      } else {
        existedLinks = this.existedLinks.filter(link => this.selectedProject.id === link.projects[0]?.ganttId && this.selectedSyncOption?.id === link.connectionType);
      }

      // check all existed links
      for (const item of existedLinks) {
        if (_.isEqual(data.filter, item.filter)) {
          this.showErrorMessage(true);
          this.disabledElements.sendButton = true;

          return false;
        }
      }

      this.showErrorMessage(false);
      this.disabledElements.sendButton = false;

      return true;
    },

    handleCreateOrUpdate() {
      const project = this.connectionType === 'all_tasks' ? 0 : { ganttId: this.selectedProject?.id, name: this.selectedProject.name };

      const data = {
        projects: [project],
        connectionType: this.connectionType,
        filter: {
          status: this.selectedStatus.length ? this.selectedStatus.map(item => +item.id) : [],
          priority: this.selectedPriority.length ? this.selectedPriority.map(item => +item.id) : [],
          assignee: this.assigneeOptions.length ? this.selectedAssignee.map(item => +item.id) : [],
          startDate: this.startDate,
          endDate: this.endDate,
        },
      };

      let analytics = {};

      if (this.connectionType !== 'all_tasks' && this.connectionType === this.assignedAndMilestones) {
        analytics = {
          sync_option: 'assigned_to_me',
          include_milestones: true,
        };
      } else if (this.connectionType === 'assigned_to_me') {
        analytics = {
          sync_option: this.connectionType,
          include_milestones: false,
        };
      } else if (this.connectionType !== 'all_tasks') {
        analytics = {
          sync_option: this.connectionType,
        };
      }
      userExtAnalytics.log('calendar_sync_saved', {
        ...analytics,
        integration_type: this.integrationName === 'google' ? 'Google Calendar' : 'Outlook Calendar',
        tab: this.connectionType === 'all_tasks' ? 'My tasks' : 'Project',
        filter: {
          status: this.selectedStatus.length ? this.selectedStatus.map(data => data.value).join(', ') : [],
          priority: this.selectedPriority.length ? this.selectedPriority.map(data => data.value).join(', ') : [],
          assignee_selected: !!this.assigneeOptions.length,
          start_date_range: `${this.startDate.start} - ${this.startDate.end}`,
          end_date_range: `${this.endDate.start} - ${this.endDate.end}`,
        },
      });

      if (this.validateData(data)) {
        this.$emit('createOrUpdateLink', data);
        this.clearData();
        this.handleCancel();
      }
    },

    removeProjectFromList(ganttId) {
      this.projects = this.projects.filter(project => project.id !== ganttId);
    },

    addProjectToList(ganttId) {
      const project = projectsModel.getItem(+ganttId);
      let projectCopy = {
        ...project,
      };

      if (!projectCopy) {
        const _project = projectsModel.getArchivedProject(+ganttId);

        projectCopy = {
          ..._project,
        };
      }

      this.projects.push(projectCopy);
    },

    onChangeProjectSelected(selectedData) {
      this.showErrorMessage(false);

      if (selectedData && selectedData[0]) {
        this.selectedProject = selectedData[0];

        if (selectedData[0] && this.selectedSyncOption) {
          this.disabledElements.sendButton = false;
        } else {
          this.disabledElements.sendButton = true;
        }

        let data = [
          {
            id: '-1',
            name: __('notAssigned'),
            img: null,
          },
        ];

        data = [...data, ...this.$store.getters['resourcesModel/getResourcesByProjectId'](selectedData[0].id).map(item => {
          item.img = item.photo;
          if (item.userId) {
            item.group = __('integration_assignee_group_real');
          } else {
            item.group = __('integration_assignee_group_nonreal');
          }

          return item;
        })];

        this.assigneeOptions = data;
      } else {
        this.selectedProject = null;
        this.disabledElements.sendButton = true;
      }

      // clear all selected assigned data
      this.selectedAssignee = [];
      this.checkAssign();
    },

    checkAssign() {
      if (this.connectionType === 'all_tasks') {
        this.disabledElements.assignee = true;
        this.disabledElements.toggleMilestones = true;
        const userResource = this.$store.getters['resourcesModel/getResourceByUserId'](user.id);

        if (this.assigneeOptions && this.assigneeOptions.length === 0) {
          this.assigneeOptions = [{ ...userResource, img: userResource.photo }];
        }
        this.selectedAssignee = [{ ...userResource, img: userResource.photo }];
      } else if (this.selectedSyncOption && (this.selectedSyncOption.id === 'assigned_to_me' || this.selectedSyncOption.id === 'assigned_to_me_and_milestones')) {
        this.disabledElements.toggleMilestones = false;
        this.disabledElements.assignee = true;
        if (this.connectionType === 'all_tasks') {
          this.disabledElements.toggleMilestones = true;
        }
        const userResourceId = this.$store.getters['resourcesModel/getResourceIdByUserId'](user.id);
        const userExistOnProject = this.assigneeOptions.find(item => item.id === userResourceId);

        this.connectionType = this.selectedSyncOption.id;
        if (userExistOnProject) {
          this.selectedAssignee = [userExistOnProject];
        } else {
          this.showErrorMessage(true, 'integration_warning_message_resource_not_assign');
          this.disabledElements.sendButton = true;
        }
      } else if (this.selectedSyncOption && this.selectedSyncOption.id) {
        this.disabledElements.assignee = false;
        this.connectionType = this.selectedSyncOption.id;
        this.selectedAssignee = [];
      } else {
        this.selectedAssignee = [];
      }
    },

    onChangeSyncSelected(selectedData) {
      this.showErrorMessage(false);
      this.selectedSyncOption = selectedData[0];

      if (selectedData[0] && selectedData[0].id !== this.assignedAndMilestones && this.toggleOn) {
        this.toggleOn = false;
      }

      if (selectedData[0] && this.selectedProject) {
        this.disabledElements.sendButton = false;
      } else {
        this.disabledElements.sendButton = true;
      }

      /*
      const existedLink = this.existedLinks.find(link => this?.selectedProject?.id === link.projects[0]?.ganttId && selectedData[0].id === link.connectionType);

      if (existedLink) {
        this.disableButton = true;
        this.syncAlreadyCreated = true;
      } else if (existedLink === undefined && !this.selectedProject) {
        this.disableButton = true;
        this.syncAlreadyCreated = false;
      } else {
        this.disableButton = false;
        this.syncAlreadyCreated = false;
      }
      */
      this.checkAssign();

      this.connectionType = selectedData[0].id;

      let analytisc = {};

      if (selectedData[0] && selectedData[0].id === this.assignedAndMilestones) {
        analytisc = {
          option_value: 'assigned_to_me',
          include_milestones: true,
        };
      } else if (selectedData[0] && selectedData[0].id === 'assigned_to_me') {
        analytisc = {
          include_milestones: false,
        };
      }
      userExtAnalytics.log('calendar_sync_tab_selected', {
        integration_type: this.integrationName === 'google' ? 'Google Calendar' : 'Outlook Calendar',
        option_value: selectedData[0].id,
        ...analytisc,
      });
    },

    onChangeStatus(selectedData) {
      this.showErrorMessage(false);
      this.selectedStatus = selectedData;

      userExtAnalytics.log('calendar_sync_filter_status_selected', {
        integration_type: this.integrationName === 'google' ? 'Google Calendar' : 'Outlook Calendar',
        status: selectedData.map(data => data.value).join(', '),
      });
      // this.disabledElements.sendButton = false;
    },

    onChangePriority(selectedData) {
      this.showErrorMessage(false);
      this.selectedPriority = selectedData;
      userExtAnalytics.log('calendar_sync_filter_priority_selected', {
        integration_type: this.integrationName === 'google' ? 'Google Calendar' : 'Outlook Calendar',
        status: selectedData.map(data => data.value).join(', '),
      });
      // this.disabledElements.sendButton = false;
    },

    onChangeAssignee(selectedData) {
      this.showErrorMessage(false);
      // this.disabledElements.sendButton = false;

      userExtAnalytics.log('calendar_sync_filter_assignee_selected', {
        integration_type: this.integrationName === 'google' ? 'Google Calendar' : 'Outlook Calendar',
        status: !!selectedData.length,
      });
      this.selectedAssignee = selectedData;
    },

    onChangeRange(value) {
      if (this.dateRangeType === 'startDate') {
        this.onChangeStartDate(value);
        userExtAnalytics.log('calendar_sync_filter_start_date_selected', {
          integration_type: this.integrationName === 'google' ? 'Google Calendar' : 'Outlook Calendar',
          start_date_range: `${value.start} - ${value.end}`,
        });
      }

      if (this.dateRangeType === 'endDate') {
        this.onChangeEndDate(value);
        userExtAnalytics.log('calendar_sync_filter_end_date_selected', {
          integration_type: this.integrationName === 'google' ? 'Google Calendar' : 'Outlook Calendar',
          end_date_range: `${value.start} - ${value.end}`,
        });
      }
    },

    onChangeStartDate(selectedData) {
      this.showErrorMessage(false);

      if (selectedData === '') return;

      if (this.selectedProject && this.selectedSyncOption) {
        this.disabledElements.sendButton = false;
      }

      if (selectedData.start === selectedData.end) {
        selectedData.end = '';
      }

      if (this.connectionType !== 'all_tasks' && this.selectedSyncOption && this.selectedSyncOption.id === 'only_milestones') {
        this.endDate = selectedData;
      }

      this.startDate = selectedData;
    },

    onChangeEndDate(selectedData) {
      this.showErrorMessage(false);

      if (selectedData === '') return;

      if (this.selectedProject && this.selectedSyncOption) {
        this.disabledElements.sendButton = false;
      }

      if (selectedData.start === selectedData.end) {
        selectedData.end = '';
      }

      this.endDate = selectedData;
    },

    clickOutsideDateRange(e) {
      if (e.target.closest('.filter_item_date')) {
        return;
      }

      if (this.isShowDateRange) {
        this.hideDateRange();
      }
    },

    showDateRangePicker(type) {
      if (this.dateRangeType === type) {
        return;
      }
      this.hideDateRange();

      if (type === 'startDate') {
        this.$refs.startDate.isActive = true;

        this.dateRangeTarget = this.$refs.startDate.$el;
        this.dateRangeType = 'startDate';
      } else {
        this.$refs.endDate.isActive = true;

        this.dateRangeTarget = this.$refs.endDate.$el;
        this.dateRangeType = 'endDate';
      }

      const micro = setTimeout(() => {
        this.isShowDateRange = true;
        clearTimeout(micro);
      }, 0);
    },

    hideDateRange(type) {
      this.isShowDateRange = false;
      this.dateRangeType = null;
      this.dateRangeTarget = null;

      this.$refs.startDate.isActive = false;
      this.$refs.endDate.isActive = false;
    },

    resetDatesValues(type) {
      if (type === 'startDate') {
        if (this.connectionType !== 'all_tasks' && this.selectedSyncOption && this.selectedSyncOption.id === 'only_milestones') {
          this.endDate = { start: '', end: '' };
        }
        this.startDate = { start: '', end: '' };
      } else {
        this.endDate = { start: '', end: '' };
      }
    },

    activateElement() {
      if (!this.toggleOn) {
        this.onChangeSyncSelected([{ ...this.selectedSyncOption, id: this.assignedAndMilestones }]);

        this.toggleOn = !this.toggleOn;
      } else {
        this.onChangeSyncSelected([{ ...this.selectedSyncOption, id: 'assigned_to_me' }]);
      }

      //       } else if (this.settingsSync.connectionType !== 'all_tasks' && option.id === 'project') {
      //         option.selected = true;
      //         this.disableSyncDrop = false;
      //         this.disableProjectDrop = false;
      //         this.connectionType = this.settingsSync.connectionType;
      //         if (this.settingsSync.connectionType === this.assignedAndMilestones) {
      //           this.selectedSyncOption = this.syncOptions.find(i => i.id === 'assigned_to_me');
      //           this.toggleOn = true;
      //         } else {
      //           this.selectedSyncOption = this.syncOptions.find(i => i.id === this.settingsSync.connectionType);
      //         }
    },
    handleCancel() {
      userExtAnalytics.log('calendar_sync_cancelled', {
        integration_type: this.integrationName === 'google' ? 'Google Calendar' : 'Outlook Calendar',
      });
      this.clearData();
      this.$emit('closePopup');
    },
    clearData() {
      this.selectedSyncOption = null;
      this.selectedProject = null;
      this.connectionType = null;
      this.syncAlreadyCreated = false;
      this.options.forEach(option => option.selected = false);
      this.toggleOn = false;
      this.disableButton = true;
    },
  },
};
</script>

<style module src="./googleOutlookIntegration.less" lang="less"></style>
